import type { QueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { loadLightDebtorsQueryFn } from 'shared/hooks/use-load-light-debtors';
import { loadLightUsersQueryFn } from 'shared/hooks/use-load-light-users';
import { loadTemplatesFn } from 'shared/hooks/use-load-templates';
import { removeAttributes } from 'shared/utils/view';
import type { TableFilter } from 'types/views';
import { z } from 'zod/v4';

import { loadFilterOptionsQueryFn } from './loadFilterOptions';

// The option shape the select inputs consume (keyText="description"/keyValue="value").
// value/id may be numeric (user ids) or string (serialised template/step ids).
export type SelectOption = { description: string; value: string | number; id: string | number };

// Everything an option loader needs to fetch + cache its select options.
type OptionLoaderContext = {
  queryClient: QueryClient;
  axiosInstance: Promise<AxiosInstance>;
  legacyAxiosInstance: Promise<AxiosInstance>;
  filter: TableFilter;
  search: string;
};

type OptionLoader = (context: OptionLoaderContext) => Promise<SelectOption[]>;

// [descriptionKey, valueKey] tuples used to serialise a few api-backed filters in the
// default loader.
const serializer: Record<string, readonly [string, string]> = {
  recovery_plan_id: ['name', 'id'],
  user_id: ['email', 'id'],
  billing_log_id: ['prefix', 'id'],
};

const loadUsers: OptionLoader = async ({ queryClient, axiosInstance }) => {
  const users = await queryClient.fetchQuery({
    queryKey: ['users', { loadInactiveUsers: false }],
    queryFn: context => loadLightUsersQueryFn(axiosInstance, context),
  });

  return users.map(user => ({ description: user.name.full, value: user.id, id: user.id }));
};

// Templates and recovery steps merged into one list, each value self-encoded as
// `{id, type}` JSON (see SELF_ENCODED_MULTI_SELECTS / filterDescriptors).
const loadStepTypes: OptionLoader = async ({ queryClient, legacyAxiosInstance }) => {
  const templates = await queryClient.fetchQuery({
    queryKey: ['templates', { stepType: false }],
    queryFn: () => loadTemplatesFn(legacyAxiosInstance, 'additional_reminder'),
  });
  const serializedTemplates = templates.map(template => ({
    description: template.name,
    value: JSON.stringify({ id: template.id, type: 'template' }),
    id: JSON.stringify({ id: template.id, type: 'template' }),
  }));

  const steps = await queryClient.fetchQuery({
    queryKey: ['filter-options', 'recovery_steps'],
    queryFn: () => loadFilterOptionsQueryFn(legacyAxiosInstance, 'recovery_steps'),
  });
  const serializedSteps = steps.map(step => {
    const { id, attributes } = step as { id: string; attributes: { name: string } };
    return {
      description: attributes.name,
      value: JSON.stringify({ id, type: 'recovery_step' }),
      id: JSON.stringify({ id, type: 'recovery_step' }),
    };
  });

  return [...serializedTemplates, ...serializedSteps];
};

const loadDebtors: OptionLoader = async ({ queryClient, axiosInstance, search }) => {
  const debtors = await queryClient.fetchQuery({
    queryKey: ['light-debtors', { query: search }],
    queryFn: context => loadLightDebtorsQueryFn(axiosInstance, context),
  });

  return debtors.map(debtor => ({ description: debtor.fullName, value: debtor.id, id: debtor.id }));
};

const LightInvoicesSchema = z.array(z.object({ id: z.coerce.string(), reference: z.string() }));

const loadInvoices: OptionLoader = async ({ queryClient, axiosInstance, search }) => {
  const invoices = await queryClient.fetchQuery({
    queryKey: ['light-invoices', { reference: search }],
    queryFn: async () => {
      const instance = await axiosInstance;
      const { data } = await instance.get('/invoices/light', { params: { reference: search } });
      return LightInvoicesSchema.parse(data);
    },
  });

  return invoices.map(({ id, reference }) => ({ description: reference, value: id, id }));
};

const loadDefault: OptionLoader = async ({ queryClient, legacyAxiosInstance, filter }) => {
  const items = await queryClient.fetchQuery({
    queryKey: ['filter-options', filter.api_url],
    queryFn: () => loadFilterOptionsQueryFn(legacyAxiosInstance, filter.api_url),
  });

  const tuple = serializer[filter.name];
  return items.map(item => {
    const record = removeAttributes(item) as Record<string, unknown>;
    const description = String(tuple ? record[tuple[0]] : record.name);
    const value = (tuple ? record[tuple[1]] : record.id) as string | number;
    return { description, value, id: record.id as string | number };
  });
};

const optionLoaders: Record<string, OptionLoader> = {
  debtor_id: loadDebtors,
  invoice_ids: loadInvoices,
  user_id: loadUsers,
  additional_user_ids: loadUsers,
  last_reminder_step_type: loadStepTypes,
};

// The loader for a filter's options — its registered recipe, or the default
// (api_url + serializer-tuple) for everything else.
export const getOptionLoader = (name: string): OptionLoader => optionLoaders[name] ?? loadDefault;
